import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, TextField, Typography } from "@mui/material";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import TriggerPoints from "../../components/TriggerPoints";
import { getStringParamsFromObject } from "../../utils";
import { INDIGO, SLATE } from "../../constants/Colors";

const TEXTAREA_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900], lineHeight: 1.6 },
};

const INITIAL_STATE = {
  degree_and_student_types: {
    display_name: "Business Context",
    prompt_input_raw: "",
    id: null,
    app_id: null,
  },
};

export default function BusinessContext() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector((state) => state?.apps?.selectedAgentSiteCode);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [state, setState] = useState(INITIAL_STATE);
  const [triggerPoint, setTriggerPoint] = useState(selectedAgentSiteCode || "");
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const originalRef = useRef(null);

  useEffect(() => {
    setPageMeta({ description: "Provide context about the degrees offered and student types served." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    const query = getStringParamsFromObject({
      app_id: currentApp.id,
      trigger_point: triggerPoint,
      types: "degree_and_student_types",
    });
    makeRequest(`/v1/controls?${query}`, { method: "GET", authTokens: tokens })
      .then((configs) => {
        let next = { ...INITIAL_STATE };
        if (Array.isArray(configs) && configs.length > 0) {
          configs.forEach((c) => {
            if (next[c.internal_name]) {
              next[c.internal_name] = { ...next[c.internal_name], prompt_input_raw: c.prompt_input_raw, id: c.id, app_id: c.app_id };
            }
          });
        }
        originalRef.current = next.degree_and_student_types.prompt_input_raw;
        setState(next);
        setIsDirty(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id, triggerPoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (type, value) => {
    setState((prev) => {
      const next = { ...prev, [type]: { ...prev[type], prompt_input_raw: value } };
      setIsDirty(originalRef.current === null || next.degree_and_student_types.prompt_input_raw !== originalRef.current);
      return next;
    });
  };

  const handleSave = async () => {
    const updatedConfigs = Object.keys(state)
      .filter((type) => state[type]?.prompt_input_raw)
      .map((type) => ({
        internal_name: type,
        display_name: state[type].display_name,
        prompt_input_raw: state[type].prompt_input_raw,
        group: "prompt_inputs",
        app_id: currentApp?.id,
        trigger_point: triggerPoint,
        ...(state[type].id ? { id: state[type].id } : {}),
      }));

    if (!updatedConfigs.length) return;

    setSaving(true);
    try {
      await makeRequest(`/v1/controls?app_id=${currentApp?.id}`, {
        method: "PUT",
        body: { controls: updatedConfigs },
        authTokens: tokens,
      });
      originalRef.current = state.degree_and_student_types.prompt_input_raw;
      setIsDirty(false);
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading Business Context..." subMessage="Fetching configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {!selectedAgentSiteCode && currentApp?.trigger_points?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <TriggerPoints app={currentApp} onChange={setTriggerPoint} triggerPoint={triggerPoint} />
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            Degree and Student Types
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], lineHeight: 1.5, mb: 2 }}>
            Provide context about the degrees offered and types of students served by your institution.
          </Typography>
          <TextField
            multiline
            minRows={20}
            maxRows={40}
            fullWidth
            name="prompt_input_raw"
            value={state.degree_and_student_types.prompt_input_raw}
            onChange={(e) => handleChange("degree_and_student_types", e.target.value)}
            placeholder={`1. E.g. Bachelor's degree programs\n2. E.g. Master's degree programs\n3. E.g. Traditional students\n4. E.g. Non-traditional students\n5. E.g. International students`}
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      <BottomActionBar
        disabledSave={!isDirty || saving}
        handleSave={handleSave}
        saving={saving}
      />
    </Box>
  );
}
